import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Trash2, Save, KeyRound, Loader2, ShieldCheck, Mail, Phone, Building2, BadgeCheck, CalendarDays, UserRound, IdCard, MapPin } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function Avatar({ src, name, size = "lg" }) {
  const dims = size === "xl" ? "h-32 w-32 text-3xl" : "h-20 w-20 text-xl";
  const initials = (name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className={`${dims} rounded-full overflow-hidden bg-[#1E88E5] text-white grid place-items-center font-semibold shadow-md`} data-testid="profile-avatar">
      {src ? <img src={src} alt={name || "avatar"} className="h-full w-full object-cover" /> : <span>{initials}</span>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      {Icon ? <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /> : null}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm font-medium text-slate-800 break-words" data-testid={testId}>{value || "—"}</div>
      </div>
    </div>
  );
}

function ProfilePicturePanel({ profile, onUpdated }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(f.type)) {
      toast.error("Only JPG, JPEG, or PNG images are allowed");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    const url = URL.createObjectURL(f);
    setPreview(url);
    setPendingFile(f);
  };

  const cancelPreview = () => {
    setPreview(null); setPendingFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const upload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post("/profile/me/picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile picture updated");
      cancelPreview();
      onUpdated?.(data.profile_picture);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    if (!profile?.profile_picture) return;
    setUploading(true);
    try {
      await api.delete("/profile/me/picture");
      toast.success("Profile picture removed");
      onUpdated?.(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <Card className="p-6">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <Avatar src={preview || profile?.profile_picture} name={profile?.name} size="xl" />
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="text-sm font-medium text-slate-800">Profile picture</div>
          <p className="text-xs text-slate-500 mt-1">JPG, JPEG or PNG · up to 5 MB. Square images render best.</p>
          <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onPick} data-testid="profile-pic-file-input" />
            {!preview && (
              <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="profile-pic-upload-btn">
                <Camera className="h-4 w-4 mr-1.5" />{profile?.profile_picture ? "Change picture" : "Upload picture"}
              </Button>
            )}
            {preview && (
              <>
                <Button size="sm" onClick={upload} disabled={uploading} className="bg-[#16A34A] hover:bg-[#15803d]" data-testid="profile-pic-save-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save
                </Button>
                <Button size="sm" variant="outline" onClick={cancelPreview} disabled={uploading} data-testid="profile-pic-cancel-btn">Cancel</Button>
              </>
            )}
            {profile?.profile_picture && !preview && (
              <Button size="sm" variant="outline" onClick={remove} disabled={uploading} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="profile-pic-remove-btn">
                <Trash2 className="h-4 w-4 mr-1.5" />Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function PersonalInfoPanel({ profile }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <UserRound className="h-5 w-5 text-[#1E88E5]" />
        <h3 className="text-base font-semibold text-slate-800">Personal Information</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
        <InfoRow icon={IdCard} label="Employee ID" value={profile?.employee_id} testId="profile-emp-id" />
        <InfoRow icon={UserRound} label="Name" value={profile?.name} testId="profile-name" />
        <InfoRow icon={BadgeCheck} label="Designation" value={profile?.designation} testId="profile-designation" />
        <InfoRow icon={Building2} label="Department" value={profile?.department} testId="profile-department" />
        <InfoRow icon={Mail} label="Email" value={profile?.email} testId="profile-email" />
        <InfoRow icon={Phone} label="Mobile" value={profile?.mobile} testId="profile-mobile" />
        <InfoRow icon={CalendarDays} label="Date of Joining" value={profile?.date_of_joining} testId="profile-doj" />
        <InfoRow icon={MapPin} label="Location" value={profile?.location} testId="profile-location" />
        <InfoRow icon={UserRound} label="Reporting Team Lead" value={profile?.team_lead_name} testId="profile-tl" />
        <InfoRow icon={UserRound} label="Reporting Manager" value={profile?.reporting_manager_name} testId="profile-mgr" />
        <InfoRow icon={ShieldCheck} label="Employment Status" value={profile?.employment_status} testId="profile-status" />
        <InfoRow icon={CalendarDays} label="Last Login" value={fmtDate(profile?.last_login_at)} testId="profile-last-login" />
      </div>
    </Card>
  );
}

function AccountSettingsPanel({ profile, onUpdated }) {
  const [mobile, setMobile] = useState(profile?.mobile || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [saving, setSaving] = useState(false);
  const emailEditable = !!profile?.email_change_allowed || ["hr", "super_admin"].includes(profile?.role);

  useEffect(() => { setMobile(profile?.mobile || ""); setEmail(profile?.email || ""); }, [profile]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (mobile !== (profile?.mobile || "")) payload.mobile = mobile;
      if (emailEditable && email !== profile?.email) payload.email = email;
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to update");
      } else {
        await api.put("/profile/me", payload);
        toast.success("Profile updated");
        onUpdated?.();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-[#1E88E5]" />
        <h3 className="text-base font-semibold text-slate-800">Account Settings</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Mobile Number</Label>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 98765 43210" data-testid="account-mobile-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">
            Email {!emailEditable && <span className="text-amber-600 normal-case">(contact HR to change)</span>}
          </Label>
          <Input type="email" value={email} disabled={!emailEditable} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-sm pt-2 border-t border-slate-100">
        <InfoRow icon={CalendarDays} label="Last Login" value={fmtDate(profile?.last_login_at)} testId="account-last-login" />
        <InfoRow icon={ShieldCheck} label="Account Status" value={profile?.active ? "Active" : "Inactive"} testId="account-status" />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="account-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save changes
        </Button>
      </div>
    </Card>
  );
}

function ChangePasswordPanel({ onChanged }) {
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const rules = [
    { ok: newPwd.length >= 8, label: "At least 8 characters" },
    { ok: /[A-Z]/.test(newPwd), label: "One uppercase letter" },
    { ok: /[a-z]/.test(newPwd), label: "One lowercase letter" },
    { ok: /[0-9]/.test(newPwd), label: "One number" },
    { ok: /[^A-Za-z0-9]/.test(newPwd), label: "One special character" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!current) return toast.error("Current password is required");
    if (newPwd !== confirm) return toast.error("New password and confirmation do not match");
    if (newPwd === current) return toast.error("New password must differ from current password");
    if (rules.some((r) => !r.ok)) return toast.error("Password does not meet policy");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPwd });
      toast.success("Password changed. You'll be signed out for security.");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <KeyRound className="h-5 w-5 text-[#1E88E5]" />
        <h3 className="text-base font-semibold text-slate-800">Change Password</h3>
      </div>
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Current Password</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" data-testid="cp-current-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">New Password</Label>
          <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} autoComplete="new-password" data-testid="cp-new-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Confirm New Password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="cp-confirm-input" />
        </div>
        <ul className="text-xs space-y-1 pt-1">
          {rules.map((r, i) => (
            <li key={r.label} className={`flex items-center gap-2 ${r.ok ? "text-emerald-600" : "text-slate-400"}`} data-testid={`cp-rule-${i}`}>
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${r.ok ? "bg-emerald-500" : "bg-slate-300"}`} />{r.label}
            </li>
          ))}
        </ul>
        <Button type="submit" disabled={saving} className="w-full sm:w-auto" data-testid="cp-submit-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />}Update password
        </Button>
      </form>
    </Card>
  );
}

export default function MyProfile() {
  const { user, refreshUser, logout } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t && ["info", "picture", "settings", "password"].includes(t)) setTab(t);
  }, [location.search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profile/me");
      setProfile(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPicUpdated = async (newDataUrl) => {
    setProfile((p) => ({ ...p, profile_picture: newDataUrl }));
    refreshUser?.();
  };

  const onProfileSaved = async () => {
    await load();
    refreshUser?.();
  };

  const onPasswordChanged = async () => {
    setTimeout(() => { logout?.(); }, 1200);
  };

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Avatar src={profile?.profile_picture} name={profile?.name} />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="profile-header-name">{profile?.name || user?.name || "—"}</h1>
            <div className="text-sm text-slate-500">{profile?.designation || ""} {profile?.department ? `· ${profile.department}` : ""}</div>
            <div className="text-xs text-slate-400 mt-0.5">{profile?.employee_id} · {profile?.email}</div>
          </div>
        </div>
      </div>

      {loading && !profile ? (
        <Card className="p-10 text-center text-slate-400">Loading profile…</Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="info" data-testid="tab-profile-info"><UserRound className="h-4 w-4 mr-1.5" />Profile Info</TabsTrigger>
            <TabsTrigger value="picture" data-testid="tab-profile-picture"><Camera className="h-4 w-4 mr-1.5" />Picture</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-account-settings"><ShieldCheck className="h-4 w-4 mr-1.5" />Account Settings</TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-change-password"><KeyRound className="h-4 w-4 mr-1.5" />Change Password</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="mt-4"><PersonalInfoPanel profile={profile} /></TabsContent>
          <TabsContent value="picture" className="mt-4"><ProfilePicturePanel profile={profile} onUpdated={onPicUpdated} /></TabsContent>
          <TabsContent value="settings" className="mt-4"><AccountSettingsPanel profile={profile} onUpdated={onProfileSaved} /></TabsContent>
          <TabsContent value="password" className="mt-4"><ChangePasswordPanel onChanged={onPasswordChanged} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
